import { FileInputProps } from 'components/Form/FileInput/FileInput';

export enum CSVRowKeys {
  TradePartnerName = 'Trade Partner Name',
  ContactPerson = 'Contact Person',
  Language = 'Language',
  EmailAddress = 'Email Address',
  Telephone = 'Telephone',
  GLN = 'GLN',
  OtherIdentifier = 'Other Identifier',
}

export type CSVRow = {
  [CSVRowKeys.TradePartnerName]: string;
  [CSVRowKeys.ContactPerson]: string;
  [CSVRowKeys.Language]: string;
  [CSVRowKeys.EmailAddress]: string;
  [CSVRowKeys.Telephone]: string;
  [CSVRowKeys.GLN]: string;
  [CSVRowKeys.OtherIdentifier]: string;
};

const hasValueOnRow = (row: any, key: CSVRowKeys) => {
  return Object.hasOwn(row, key) && row[key] !== '';
};
const validateCSVRow = (row: unknown) => {
  if (!row) {
    return false;
  }
  return (
    hasValueOnRow(row, CSVRowKeys.TradePartnerName) &&
    hasValueOnRow(row, CSVRowKeys.ContactPerson) &&
    hasValueOnRow(row, CSVRowKeys.Language) &&
    hasValueOnRow(row, CSVRowKeys.EmailAddress) &&
    hasValueOnRow(row, CSVRowKeys.Telephone) &&
    (hasValueOnRow(row, CSVRowKeys.GLN) || hasValueOnRow(row, CSVRowKeys.OtherIdentifier))
  );
};
/**
 * Possible error codes returned by the validateCSV function:
 * - 'invalid_csv': The CSV file is invalid or does not meet the required format.
 */
type ValidateCSVErrorCode = 'invalid_csv';

export const validateCSV: FileInputProps['validate'] = (results: Array<unknown> | unknown) => {
  if (Array.isArray(results) && results.length > 0 && results.every(validateCSVRow)) {
    return { success: true };
  }
  return { success: false, errorCode: 'invalid_csv' as ValidateCSVErrorCode };
};

export type CSVValidationErrorDetail = {
  row: number;
  column: string;
  value: string;
  expectedFormat: string;
};

export const collectCSVValidationErrors = (results: Array<unknown> | unknown): Array<CSVValidationErrorDetail> => {
  const errorDetails: Array<CSVValidationErrorDetail> = [];

  if (!Array.isArray(results)) {
    return errorDetails;
  }

  results.forEach((row, index) => {
    const rowNumber = index + 1;

    if (!row || typeof row !== 'object') {
      return;
    }

    if (!hasValueOnRow(row, CSVRowKeys.TradePartnerName)) {
      errorDetails.push({
        row: rowNumber,
        column: CSVRowKeys.TradePartnerName,
        value: '',
        expectedFormat: 'required',
      });
    }

    if (!hasValueOnRow(row, CSVRowKeys.ContactPerson)) {
      errorDetails.push({
        row: rowNumber,
        column: CSVRowKeys.ContactPerson,
        value: '',
        expectedFormat: 'required',
      });
    }

    if (!hasValueOnRow(row, CSVRowKeys.Language)) {
      errorDetails.push({
        row: rowNumber,
        column: CSVRowKeys.Language,
        value: '',
        expectedFormat: 'required',
      });
    }

    if (!hasValueOnRow(row, CSVRowKeys.EmailAddress)) {
      errorDetails.push({
        row: rowNumber,
        column: CSVRowKeys.EmailAddress,
        value: '',
        expectedFormat: 'required',
      });
    }

    if (!hasValueOnRow(row, CSVRowKeys.Telephone)) {
      errorDetails.push({
        row: rowNumber,
        column: CSVRowKeys.Telephone,
        value: '',
        expectedFormat: 'required',
      });
    }

    const hasGLN = hasValueOnRow(row, CSVRowKeys.GLN);
    const hasOtherIdentifier = hasValueOnRow(row, CSVRowKeys.OtherIdentifier);

    if (!hasGLN && !hasOtherIdentifier) {
      errorDetails.push({
        row: rowNumber,
        column: `${CSVRowKeys.GLN} or ${CSVRowKeys.OtherIdentifier}`,
        value: '',
        expectedFormat: 'atLeastOneRequired',
      });
    }
  });

  return errorDetails;
};
